import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Info, RefreshCw, Search, DollarSign, Calendar, Clock, Trash2, Plus, Zap, Check, PauseCircle, X, ArrowDownLeft, ArrowUpRight } from 'react-feather';
import { apiClient } from '../../shared/api/client';
import { parseAmount } from '../../shared/utils/amount';

interface Rule {
  id: number;
  description: string;
  textMatch?: string | null;
  category: string | null;
  categoryIds: number[];
  priority: number;
  matchCount: number;
  isActive: boolean;
  isRecurring: boolean;
  recurringType?: string | null;
  expectedAmount?: number | null;
  nextExpectedDate?: string | null;
  lastMatchedDate?: string | null;
  amountMin?: number | null;
  amountMax?: number | null;
  direction?: 'ANY' | 'INCOME' | 'EXPENSE';
  isReserveOnly?: boolean;
}

interface CategoryOption {
  id: number;
  name: string;
}

const emptyForm = {
  description: '',
  textMatch: '',
  renameTo: '',
  amountMin: '',
  direction: 'ANY',
  categoryId: '',
  priority: '3',
  isRecurring: false,
  recurringType: 'MONTHLY',
  expectedAmount: '',
  nextExpectedDate: '',
};

const RulesPage: React.FC = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'vanliga' | 'återkommande'>('vanliga');
  const [rules, setRules] = useState<Rule[]>([]);
  const [categories, setCategories] = useState<CategoryOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [applying, setApplying] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [rulesRes, categoriesRes] = await Promise.all([
        apiClient.get('/rules'),
        apiClient.get('/categories'),
      ]);
      setRules((rulesRes as any).rules || []);
      const cats = ((categoriesRes as any).categories || []).flatMap((c: any) => [
        { id: c.id, name: c.name },
        ...(c.children || []).map((ch: any) => ({ id: ch.id, name: `${c.name} › ${ch.name}` })),
      ]);
      setCategories(cats);
    } catch (err) {
      console.error('Failed to load rules:', err);
      setError('Kunde inte hämta regler. Försök igen.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.description || (!form.textMatch && !form.amountMin)) return;
    try {
      setSaving(true);
      const response = await apiClient.post('/rules', {
        description: form.description,
        textMatch: form.textMatch || null,
        amountMin: form.amountMin ? parseAmount(form.amountMin) : null,
        renameTo: form.renameTo.trim() || null,
        direction: form.direction,
        categoryIds: form.categoryId ? [parseInt(form.categoryId)] : [],
        priority: parseInt(form.priority),
        isRecurring: form.isRecurring,
        recurringType: form.isRecurring ? form.recurringType : null,
        expectedAmount: form.isRecurring && form.expectedAmount ? parseAmount(form.expectedAmount) : null,
        nextExpectedDate: form.isRecurring && form.nextExpectedDate ? form.nextExpectedDate : null,
      });
      const categorized = (response as any).categorized || 0;
      setInfo(categorized > 0
        ? `Regel skapad — ${categorized} befintliga transaktioner kategoriserades direkt!`
        : 'Regel skapad. Den appliceras automatiskt på nya transaktioner vid varje banksynk.');
      setForm(emptyForm);
      setShowForm(false);
      await loadData();
    } catch (err) {
      console.error('Failed to create rule:', err);
      setError('Kunde inte spara regeln. Försök igen.');
    } finally {
      setSaving(false);
    }
  };

  const handleApplyAll = async () => {
    try {
      setApplying(true);
      setError(null);
      const response = await apiClient.post('/rules/apply');
      setInfo((response as any).message || 'Regler applicerade');
      await loadData();
    } catch (err) {
      console.error('Failed to apply rules:', err);
      setError('Kunde inte applicera reglerna.');
    } finally {
      setApplying(false);
    }
  };

  const handleToggle = async (rule: Rule) => {
    try {
      await apiClient.post(`/rules/${rule.id}/toggle`);
      await loadData();
    } catch (err) {
      console.error('Failed to toggle rule:', err);
      setError('Kunde inte ändra regelns status.');
    }
  };

  const handleDelete = async (rule: Rule) => {
    if (!window.confirm(`Ta bort regeln "${rule.description}"?`)) return;
    try {
      await apiClient.delete(`/rules/${rule.id}`);
      await loadData();
    } catch (err) {
      console.error('Failed to delete rule:', err);
      setError('Kunde inte ta bort regeln.');
    }
  };

  const getPriorityColor = (priority: number) => {
    if (priority <= 2) return 'bg-red-500';
    if (priority <= 4) return 'bg-yellow-500';
    return 'bg-green-500';
  };

  const getPriorityText = (priority: number) => {
    if (priority <= 2) return 'Hög';
    if (priority <= 4) return 'Medel';
    return 'Låg';
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('sv-SE', {
      day: 'numeric',
      month: 'short',
    });
  };

  const formatAmount = (amount: number) => {
    return new Intl.NumberFormat('sv-SE', {
      style: 'currency',
      currency: 'SEK',
      minimumFractionDigits: 0,
    }).format(amount);
  };

  const renderRule = (rule: Rule) => (
    <div key={rule.id} className="bg-white rounded-xl p-4 shadow-sm mb-3">
      <div className="flex justify-between items-start mb-3">
        <div className="flex-1">
          <div className="flex items-center mb-1">
            <h3 className="text-base font-semibold text-gray-900 mr-2">{rule.description}</h3>
            {rule.isRecurring && (
              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-50 text-blue-600">
                <RefreshCw size={11} className="mr-1" />Återkommande
              </span>
            )}
          </div>
          {rule.category && (
            <p className="text-sm text-blue-600 font-medium">→ {rule.category}</p>
          )}
        </div>
        <div className="flex items-center">
          <span className={`px-2 py-1 rounded-full text-xs font-semibold text-white mr-2 ${getPriorityColor(rule.priority)}`}>
            {getPriorityText(rule.priority)}
          </span>
          <button
            onClick={() => handleToggle(rule)}
            className={`text-lg ${rule.isActive ? 'text-green-500' : 'text-gray-400'}`}
            title={rule.isActive ? 'Aktiv – klicka för att pausa' : 'Pausad – klicka för att aktivera'}
          >
            {rule.isActive ? <Check size={18} /> : <PauseCircle size={18} />}
          </button>
        </div>
      </div>

      <div className="mb-3">
        {rule.isReserveOnly && (
          <div className="flex items-center mb-1">
            <Info size={14} className="text-gray-500 mr-2" />
            <span className="text-sm text-gray-600">
              Reserv - matchar inga transaktioner, lägger bara undan pengar varje månad
            </span>
          </div>
        )}
        {rule.textMatch && (
          <div className="flex items-center mb-1">
            <Search size={14} className="text-gray-500 mr-2" />
            <span className="text-sm text-gray-600">
              Text innehåller: "{rule.textMatch}"
            </span>
          </div>
        )}
        {rule.direction && rule.direction !== 'ANY' && (
          <div className="flex items-center mb-1">
            {rule.direction === 'INCOME'
              ? <ArrowDownLeft size={14} className="text-gray-500 mr-2" />
              : <ArrowUpRight size={14} className="text-gray-500 mr-2" />}
            <span className="text-sm text-gray-600">
              {rule.direction === 'INCOME' ? 'Bara pengar in' : 'Bara pengar ut'}
            </span>
          </div>
        )}
        {rule.amountMin != null && (
          <div className="flex items-center mb-1">
            <DollarSign size={14} className="text-gray-500 mr-2" />
            <span className="text-sm text-gray-600">
              Belopp från: {rule.amountMin.toLocaleString('sv-SE')} kr
            </span>
          </div>
        )}
      </div>

      {/* Återkommande prognos information */}
      {rule.isRecurring && (rule.nextExpectedDate || rule.lastMatchedDate) && (
        <div className="bg-gray-50 rounded-lg p-3 mb-3">
          {rule.nextExpectedDate && (
            <div className="flex items-center mb-1">
              <Calendar size={14} className="text-blue-600 mr-2" />
              <span className="text-sm text-gray-600 mr-2">Nästa förväntas:</span>
              <span className="text-sm font-medium text-gray-900">
                {formatDate(rule.nextExpectedDate)}
                {rule.expectedAmount != null && ` (${formatAmount(rule.expectedAmount)})`}
              </span>
            </div>
          )}
          {rule.lastMatchedDate && (
            <div className="flex items-center">
              <Clock size={14} className="text-gray-500 mr-2" />
              <span className="text-sm text-gray-600 mr-2">Senaste:</span>
              <span className="text-sm font-medium text-gray-900">
                {formatDate(rule.lastMatchedDate)}
              </span>
            </div>
          )}
        </div>
      )}

      <div className="flex justify-between items-center pt-3 border-t border-gray-100">
        <span className="text-sm text-gray-500">
          {rule.matchCount} matchningar
        </span>
        <button
          onClick={() => handleDelete(rule)}
          className="p-2 text-gray-400 hover:text-red-500 transition-colors"
        >
          <Trash2 size={16} />
        </button>
      </div>
    </div>
  );

  // Filtrera regler baserat på aktiv tab
  const vanligaRules = rules.filter(rule => !rule.isRecurring);
  const återkommandeRules = rules.filter(rule => rule.isRecurring);

  const currentRules = activeTab === 'vanliga' ? vanligaRules : återkommandeRules;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="page-header bg-white shadow-sm border-b border-gray-200">
        <div className="page-header-content px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <button onClick={() => navigate('/settings')} className="mr-4">
                <span className="text-xl">‹</span>
              </button>
              <h1 className="text-xl font-bold text-gray-900">Regler</h1>
            </div>
            <button
              onClick={() => navigate('/settings/rules/info')}
              className="w-8 h-8 bg-blue-50 rounded-full flex items-center justify-center hover:bg-blue-100 transition-colors"
              title="Läs mer om hur regler fungerar"
            >
              <Info size={16} className="text-blue-500" />
            </button>
          </div>
        </div>
      </div>

      <div className="page-content px-4 py-6">
        <p className="text-gray-600 mb-6">Automatisk kategorisering av transaktioner</p>

        {error && (
          <div className="bg-red-50 border-l-4 border-red-400 p-4 mb-6 rounded-lg">
            <span className="text-red-800 text-sm">{error}</span>
          </div>
        )}

        {info && (
          <div className="bg-green-50 border-l-4 border-green-400 p-4 mb-6 rounded-lg flex justify-between items-center">
            <span className="text-green-800 text-sm">{info}</span>
            <button onClick={() => setInfo(null)} className="text-green-600 ml-3"><X size={14} /></button>
          </div>
        )}

        {loading ? (
          <div className="text-center py-12">
            <p className="text-gray-500">Laddar regler…</p>
          </div>
        ) : (
          <>
            {/* Tab Navigation */}
            <div className="bg-white rounded-xl p-1 mb-4 shadow-sm">
              <div className="flex">
                <button
                  className={`flex-1 py-3 px-4 rounded-lg text-sm font-medium transition-colors ${
                    activeTab === 'vanliga'
                      ? 'bg-blue-600 text-white'
                      : 'text-gray-600 hover:text-gray-800'
                  }`}
                  onClick={() => setActiveTab('vanliga')}
                >
                  Vanliga ({vanligaRules.length})
                </button>
                <button
                  className={`flex-1 py-3 px-4 rounded-lg text-sm font-medium transition-colors ${
                    activeTab === 'återkommande'
                      ? 'bg-blue-600 text-white'
                      : 'text-gray-600 hover:text-gray-800'
                  }`}
                  onClick={() => setActiveTab('återkommande')}
                >
                  Återkommande ({återkommandeRules.length})
                </button>
              </div>
            </div>

            {/* Rules List */}
            <div className="mb-6">
              {currentRules.length > 0 ? (
                currentRules.map(renderRule)
              ) : (
                <div className="text-center py-12 bg-white rounded-xl">
                  <span className="mb-4 block">
                    {activeTab === 'vanliga' ? <Search size={44} className="mx-auto text-gray-300" /> : <RefreshCw size={44} className="mx-auto text-gray-300" />}
                  </span>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    {activeTab === 'vanliga' ? 'Inga vanliga regler' : 'Inga återkommande regler'}
                  </h3>
                  <p className="text-gray-600">
                    {activeTab === 'vanliga'
                      ? 'Skapa regler för automatisk kategorisering'
                      : 'Skapa regler för återkommande transaktioner'
                    }
                  </p>
                </div>
              )}
            </div>

            {/* Add Rule Form */}
            {showForm ? (
              <form onSubmit={handleCreate} className="bg-white rounded-xl p-5 shadow-sm">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Ny regel</h3>
                <div className="space-y-3">
                  <input
                    type="text"
                    placeholder="Beskrivning (t.ex. Netflix → Underhållning)"
                    value={form.description}
                    onChange={e => setForm({ ...form, description: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                    required
                  />
                  <input
                    type="text"
                    placeholder="Eget namn i listorna (valfritt, t.ex. Farmors Café)"
                    value={form.renameTo}
                    onChange={e => setForm({ ...form, renameTo: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  />
                  <div className="grid grid-cols-2 gap-3">
                    <input
                      type="text"
                      placeholder="Text som matchar (t.ex. netflix)"
                      value={form.textMatch}
                      onChange={e => setForm({ ...form, textMatch: e.target.value })}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2"
                    />
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      placeholder="Belopp från (kr)"
                      value={form.amountMin}
                      onChange={e => setForm({ ...form, amountMin: e.target.value })}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2"
                    />
                  </div>
                  <select
                    value={form.direction}
                    onChange={e => setForm({ ...form, direction: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 bg-white"
                  >
                    <option value="ANY">Gäller både pengar in och ut</option>
                    <option value="EXPENSE">Bara pengar ut</option>
                    <option value="INCOME">Bara pengar in</option>
                  </select>
                  <div className="grid grid-cols-2 gap-3">
                    <select
                      value={form.categoryId}
                      onChange={e => setForm({ ...form, categoryId: e.target.value })}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 bg-white"
                    >
                      <option value="">Välj kategori</option>
                      {categories.map(c => (
                        <option key={c.id} value={c.id}>{c.name}</option>
                      ))}
                    </select>
                    <select
                      value={form.priority}
                      onChange={e => setForm({ ...form, priority: e.target.value })}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 bg-white"
                    >
                      <option value="1">Hög prioritet</option>
                      <option value="3">Medel prioritet</option>
                      <option value="5">Låg prioritet</option>
                    </select>
                  </div>
                  <label className="flex items-center text-sm text-gray-700">
                    <input
                      type="checkbox"
                      checked={form.isRecurring}
                      onChange={e => setForm({ ...form, isRecurring: e.target.checked })}
                      className="mr-2"
                    />
                    <RefreshCw size={13} className="inline mr-1" /> Återkommande transaktion (används i prognosen)
                  </label>
                  {form.isRecurring && (
                    <div className="grid grid-cols-3 gap-3">
                      <select
                        value={form.recurringType}
                        onChange={e => setForm({ ...form, recurringType: e.target.value })}
                        className="border border-gray-300 rounded-lg px-3 py-2 bg-white text-sm"
                      >
                        <option value="MONTHLY">Varje månad</option>
                        <option value="QUARTERLY">Varje kvartal</option>
                        <option value="YEARLY">Varje år</option>
                      </select>
                      <input
                        type="number"
                        step="0.01"
                        placeholder="Förväntat belopp"
                        value={form.expectedAmount}
                        onChange={e => setForm({ ...form, expectedAmount: e.target.value })}
                        className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
                      />
                      <input
                        type="date"
                        value={form.nextExpectedDate}
                        onChange={e => setForm({ ...form, nextExpectedDate: e.target.value })}
                        className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
                      />
                    </div>
                  )}
                </div>
                <div className="flex gap-3 mt-4">
                  <button
                    type="submit"
                    disabled={saving}
                    className="flex-1 bg-blue-600 text-white py-3 rounded-xl font-semibold hover:bg-blue-700 transition-colors disabled:opacity-50"
                  >
                    {saving ? 'Sparar…' : 'Spara regel'}
                  </button>
                  <button
                    type="button"
                    onClick={() => { setShowForm(false); setForm(emptyForm); }}
                    className="flex-1 bg-gray-100 text-gray-700 py-3 rounded-xl font-semibold hover:bg-gray-200 transition-colors"
                  >
                    Avbryt
                  </button>
                </div>
              </form>
            ) : (
              <>
                <button
                  onClick={() => setShowForm(true)}
                  className="w-full bg-white border-2 border-dashed border-blue-600 text-blue-600 py-4 rounded-xl font-semibold hover:bg-blue-50 transition-colors"
                >
                  <Plus size={16} className="inline mr-1 -mt-0.5" />Skapa ny regel
                </button>
                {rules.length > 0 && (
                  <button
                    onClick={handleApplyAll}
                    disabled={applying}
                    className="w-full bg-gray-100 text-gray-700 py-3 rounded-xl font-medium mt-3 hover:bg-gray-200 transition-colors disabled:opacity-50"
                  >
                    {applying ? 'Applicerar…' : <><Zap size={14} className="inline mr-1 -mt-0.5" />Applicera regler på okategoriserade transaktioner</>}
                  </button>
                )}
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default RulesPage;
